import { Injectable } from '@angular/core';
import { Cell, Column, Workbook, Worksheet } from 'exceljs';
import { ExportData, ExportDataField, ExportDataRow } from '../../common-components/export/export.model';
import { TFontDictionary, TFontFamilyTypes } from 'pdfmake/interfaces';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
export interface ABCD {
    [fontName: string]: TFontFamilyTypes;
}

@Injectable()
export class ExportService {

    constructor() { }

    /**
     *
     * @param content the binary content
     * @param type The type of the file to be generated like 'text/csv' etc
     */
    saveFile(content: any, type: string, fileName: string) {
        const blob = new Blob([content], { type: type });
        const a = document.createElement('a');
        const url = URL.createObjectURL(blob);
        a.href = url
        a.download = fileName;
        a.click();

        setTimeout(() => {
            if (blob) {
                // alert(url);
                URL.revokeObjectURL(url);
            }
            if (a) {
                a.remove();
            }
        });
    }
    exportToExcel(data: any[][], worksheetName: string,
        fileName: string, headerStyles: { backgroundColor: string; fontColor: string; bold: boolean }) {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet(worksheetName);

        // Flatten the data
        const flattenedData = data.map(row => {
            const flattenedRow: any = {};
            row.forEach(cell => {
                flattenedRow[cell.fieldName] = cell.value;
            });
            return flattenedRow;
        });

        // Define columns based on field names dynamically
        worksheet.columns = Object.keys(flattenedData[0]).map(key => ({
            header: key, // Header name
            key: key,    // Property key in the row object
            width: 20    // Optional column width
        }));
        // Apply header styles
        const headerRow = worksheet.getRow(1); // Header is the first row
        headerRow.eachCell((cell: Cell) => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: headerStyles.backgroundColor.replace('#', '') } // Background color
            };
            cell.font = {
                color: { argb: headerStyles.fontColor.replace('#', '') }, // Font color
                bold: headerStyles.bold // Bold font
            };
            cell.alignment = { horizontal: 'center', vertical: 'middle' }; // Optional alignment
        });
        // Add rows to the worksheet
        worksheet.addRows(flattenedData);

        // Apply center alignment to all cells (excluding headers)
        worksheet.eachRow((row, rowNumber) => {
            row.eachCell((cell: Cell) => {
                cell.alignment = { horizontal: 'center', vertical: 'middle' }; // Center align all cells
            });
        });

        // Generate Excel file and save it
        workbook.xlsx.writeBuffer().then((buffer) => {
            this.saveFile(buffer, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', `${fileName}.xlsx`);
        });
    }

    exportToPDF(data: ExportData, fileName: string,
        headerStyles: { backgroundColor: string; fontColor: string; bold: boolean }, orientation: 'portrait' | 'landscape' = 'portrait') {

        if (!data || data?.length === 0) {
            console.error("No data to export");
            return;
        }
        // 1. Get all column names
        const allColumns = (data[0] as ExportDataRow).map(field => field.fieldName);
        const maxColumnsPerPage = orientation === 'landscape' ? 6 : 5;

        // 2. Split columns into chunks
        const columnChunks: string[][] = [];
        for (let i = 0; i < allColumns.length; i += maxColumnsPerPage) {
            columnChunks.push(allColumns.slice(i, i + maxColumnsPerPage));
        }

        // 3. Generate content for each chunk
        const content: any[] = [
            { text: fileName, style: 'header', alignment: 'center', margin: [0, 0, 0, 0] }
        ];


        columnChunks.forEach((chunk, index) => {
            const tableObj = this.getPDFTable(data, headerStyles, chunk);

        // Modify each cell to have center alignment
        tableObj.body.forEach((row: any) => {
            row.forEach((cell: any) => {
                cell.alignment = 'center'; // Explicitly set alignment for each cell
            });
        });

        content.push({
            table: tableObj,
            layout: 'lightHorizontalLines',
            alignment: 'center',
            margin: [0, 0, 0, 0]
        });

             // Add page break unless it's the last chunk
             if (index < columnChunks.length - 1) {
                content.push({ text: '', pageBreak: 'after' });
            }
        });


        const documentDefinition = {
            pageOrientation: orientation, // Set portrait or landscape dynamically
            pageSize:'A4',
            content,
            styles: {
                header: {
                    background: headerStyles.backgroundColor,
                    color: headerStyles.fontColor,
                    bold: true,
                    alignment: 'center'
                },
                tableCell: {
                    alignment: 'center' // Center align all table cells
                },
                defaultStyle: {
                    alignment: 'center' // Apply to all text elements by default
                }
            },
        };

        // Generate and download the PDF
        pdfMake.createPdf(documentDefinition).download(`${fileName}.pdf`);
    }

    /**
     * Method that creates the column definition for exceljs Worksheet.
     * and also creates and add a new row to the Worksheet.
     * Returns the array of Partial<Column>.
     */
    private getColumnsDefinition(data: ExportData, worksheet: Worksheet): Partial<Column>[] {
        let result: Partial<Column>[] = [];
        for (let i = 0; i < data.length; i++) {
            const rowObj: any = {};
            for (let j = 0; j < ((data[i] as ExportDataRow)).length; j++) {
                const expDataField = ((data[i] as ExportDataRow)[j] as ExportDataField);

                const column: Column = {
                    header: expDataField.fieldName,
                    key: expDataField.fieldName.toLowerCase(),
                    outlineLevel: 0,
                    hidden: false,
                    style: {},
                    values: expDataField.value,
                    letter: '',
                    number: 0,
                    worksheet: worksheet,
                    isCustomWidth: false,
                    headers: [],
                    isDefault: false,
                    headerCount: 0,
                    equivalentTo: (other: Column): boolean => true,
                    eachCell: (callback: any): void => { },
                    collapsed: false,
                    defn: undefined
                };

                // create row data field
                rowObj[expDataField.fieldName.toLowerCase()] = expDataField.value;

                result.push(column);
            }

            // keep adding the row
            worksheet.addRow(rowObj);
        }

        return result;
    }

    public initializePdfmakeAndGetDocument(title: string): any {
        (pdfMake as any).vfs = pdfFonts.pdfMake.vfs;
        return {
            content: [
                { text: title, style: 'header', margin: [0, 0, 0, 0] }
            ],
            styles: {
                header: {
                    fontSize: 18,
                    bold: true,
                    background: 'red'
                },
                tableHeader: {
                    bold: true,
                    fontSize: 12,
                    color: 'black'
                }
            }
        };;
    }

    private getPDFTable(data: ExportData, headerStyles: {
        backgroundColor: string;
        fontColor: string; bold: boolean
    }, columnChunk: string[]): any {
        if (!data || data.length === 0) {
            return { body: [['No Data Available']] };
        }
        const table: any = {
            headerRows: 1,
            widths: Array(columnChunk?.length).fill('*'),
            body: []
        };

        // Define headers
        const headerRow: any[] = columnChunk.map((col: any) => ({
            text: col.toUpperCase(),
            bold: headerStyles.bold,
            fillColor: headerStyles.backgroundColor,
            color: headerStyles.fontColor,
            alignment: 'center',
        }));

        table.body.push(headerRow);
        // Define table rows
        for (let i = 0; i < data.length; i++) {
            const rowData: any[] = columnChunk.map(col => {
                const field = (data[i] as ExportDataRow).find(f => f.fieldName === col);
                return {
                    text: field?.value ?? '',
                    alignment: 'left'
                };
            });
            table.body.push(rowData);
        }
        return table;
    }
}
